/**
 * Wake turbulence separation matrix.
 *
 * Sources:
 *   - JO 7110.65, Section 5-5-4 (Wake Turbulence Application)
 *   - JO 7110.65, Section 3-9-6 (Same Runway / Intersecting Runway departures)
 *
 * Aircraft weight categories per the .65 / ICAO:
 *
 *   SUPER  — Airbus A380, Antonov An-225 (>1,300,000 lb MTOW)
 *   HEAVY  — > 300,000 lb MTOW
 *   B757   — Boeing 757 (broken out separately due to its disproportionate wake)
 *   LARGE  — 41,001 lb to 300,000 lb MTOW (most jet airliners)
 *   SMALL+ — 12,500 to 41,000 lb MTOW with two or more turbojet/turbofan engines
 *   SMALL  — 12,500 lb or less MTOW
 *
 * The values below are in nautical miles for in-trail radar separation;
 * a small subset (same/parallel/crossing runway under departure ops)
 * uses time-based separation expressed in minutes — see `runwayMinutes`.
 *
 * Where the .65 doesn't specify a wake minimum, the standard 3 NM radar
 * separation applies, indicated by `null` in the matrix.
 */

export type WakeCategory = "super" | "heavy" | "b757" | "large" | "small+" | "small";

export const CATEGORY_ORDER: WakeCategory[] = [
  "super",
  "heavy",
  "b757",
  "large",
  "small+",
  "small",
];

export const CATEGORY_LABELS: Record<WakeCategory, string> = {
  super: "Super",
  heavy: "Heavy",
  b757: "B757",
  large: "Large",
  "small+": "Small+",
  small: "Small",
};

export const CATEGORY_HINTS: Record<WakeCategory, string> = {
  super: "A380 or An-225 (MTOW >1.3M lb).",
  heavy: "MTOW > 300,000 lb. Most widebodies (B777, B747, A330, A350).",
  b757: "Boeing 757 (broken out — wake larger than weight alone implies).",
  large: "MTOW 41,001 – 300,000 lb. Most narrowbodies (B737, A320).",
  "small+": "MTOW 12,500 – 41,000 lb, multi-engine turbojet/turbofan.",
  small: "MTOW ≤ 12,500 lb. GA, small turboprop, light jets.",
};

/**
 * In-trail radar separation (NM) for a TRAILING aircraft following the
 * LEADING aircraft. Per JO 7110.65 Section 5-5-4.
 *
 *   matrix[leading][trailing] = NM (or null = standard 3 NM radar)
 */
export const IN_TRAIL_NM: Record<WakeCategory, Partial<Record<WakeCategory, number>>> = {
  super: {
    heavy: 6,
    b757: 7,
    large: 7,
    "small+": 8,
    small: 8,
  },
  heavy: {
    heavy: 4,
    b757: 5,
    large: 5,
    "small+": 5,
    small: 6,
  },
  b757: {
    heavy: 4,
    b757: 4,
    large: 4,
    "small+": 4,
    small: 5,
  },
  large: {
    // 4 NM only when a Small follows a Large
    small: 4,
  },
  "small+": {},
  small: {},
};

/**
 * Same-runway, intersecting-runway, and closely-spaced parallel runway
 * MINIMUM SEPARATION IN MINUTES, per JO 7110.65 Section 3-9-6.
 *
 * Applied when the trailing aircraft will rotate from a runway location
 * that subjects it to the leading aircraft's wake at takeoff. Most
 * commonly used in same-runway full-length departures.
 */
export const RUNWAY_MINUTES: Record<WakeCategory, Partial<Record<WakeCategory, number>>> = {
  super: {
    heavy: 3,
    b757: 3,
    large: 3,
    "small+": 3,
    small: 3,
  },
  heavy: {
    // Heavy behind Heavy: 2 min same-runway
    heavy: 2,
    b757: 2,
    large: 2,
    "small+": 2,
    small: 2,
  },
  b757: {
    heavy: 2,
    b757: 2,
    large: 2,
    "small+": 2,
    small: 2,
  },
  large: {
    small: 3,
  },
  "small+": {},
  small: {},
};

/**
 * Get the required separation for a given leading/trailing pair, in the
 * specified scenario (in-trail or runway-based). Returns null when the
 * standard 3 NM radar separation (no wake minimum) applies.
 */
export function requiredSeparation(
  leading: WakeCategory,
  trailing: WakeCategory,
  scenario: "in-trail" | "runway",
): { value: number; unit: "NM" | "min" } | null {
  if (scenario === "in-trail") {
    const v = IN_TRAIL_NM[leading]?.[trailing];
    return typeof v === "number" ? { value: v, unit: "NM" } : null;
  }
  const v = RUNWAY_MINUTES[leading]?.[trailing];
  return typeof v === "number" ? { value: v, unit: "min" } : null;
}
